import * as React from 'react';
import { ConfirmationDialog } from './ConfirmationDialog';
import { SourceType, SourceType_ } from '../../store/ExternalEpmConnectStore';
import { ProjectInfo } from '../../store/ProjectsListStore';
import { post } from '../../fetch-interceptor';
import { Dictionary, getPluralSuffix } from '../../entities/common';
import { DetailsSpinner } from './Spinner';
import { Link, Spinner } from 'office-ui-fabric-react';

const PPMX_TASKS_LIMIT = 1999;

type Statistics = Dictionary<{ [k: number]: number }>;

type OwnProps = {
    projects: ProjectInfo[];
    sourceType: SourceType;
    onConfirm: (projectIds: string[], sourceType: SourceType) => void;
    onDismiss: () => void;
}

const CopyScheduleConfirmationDialog = (props: OwnProps) => {
    const { projects, sourceType } = props;
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [statistics, setStatistics] = React.useState<Statistics>();

    React.useEffect(() => {
        post<Statistics>(`/api/project/task/statistics`, { ids: projects.filter(_ => _.isEditable).map(_ => _.id) })
            .then((data) => { setStatistics(data); })
            .finally(() => setIsLoading(false));

        setIsLoading(true);
    }, projects);

    const { validIds, message } = messageBuilder(projects, sourceType, statistics)
    if(isLoading) {
        return <div className='copy-schedule-dialog'>
            <DetailsSpinner isLoading={isLoading}/>
        </div>
    }    
    return <ConfirmationDialog
        onDismiss={props.onDismiss}
        onYes={validIds?.length ? () => props.onConfirm(validIds, sourceType) : props.onDismiss}
        onNo={validIds?.length ? props.onDismiss : undefined}
        yesButtonProps={{ text: validIds?.length ? "Copy" : "Got it" }}
        noButtonProps={{ text: "Cancel" }}
        dialogContentProps={{ title: isLoading || validIds?.length ? "Copy to PPM Express Tasks"  : "Unable to copy tasks" }}>
        {message}
    </ConfirmationDialog>
};

const getTasksCount = (stats: { [k: number]: number }, sourceType: SourceType): number => (stats?.[sourceType] ?? 0);

type ProjectsTaskSummary = { validIds: string[], exceedLimit: string[], readonly: string[], empty: string[] }
const messageBuilder = (projects: ProjectInfo[], sourceType: SourceType, stats?: Statistics): { validIds: string[], message: JSX.Element } => {
    if (!stats) {
        return { validIds: [], message: <Spinner label="Loading..." /> };
    }
    if (projects.length === 0) {
        return { validIds: [], message: <span>No projects selected for copying.</span> };
    }
    const itemName = [SourceType.Jira, SourceType.VSTS].includes(sourceType) ? "work items" : "tasks";

    const summary = projects.reduce<ProjectsTaskSummary>((acc, project) => {
        const projectStats = stats[project.id]; 
        if (!project.isEditable) {
            acc.readonly.push(project.attributes.Name);
        } else if (getTasksCount(projectStats, sourceType) === 0) {
            acc.empty.push(project.attributes.Name);
        } else if (getTasksCount(projectStats, sourceType) + getTasksCount(projectStats, SourceType.Ppmx) > PPMX_TASKS_LIMIT) {
            acc.exceedLimit.push(project.attributes.Name);
        } else {
            acc.validIds.push(project.id);
        }
        return acc;
    }, { validIds: [], exceedLimit: [], readonly: [], empty: [] });

    if (projects.length === 1) {
        if (summary.readonly.length) {
            return { validIds: [], message: <p>Synced {itemName} cannot be copied for the following project, since you don’t have edit permissions</p> };
        }
        if (summary.empty.length) {
            return { validIds: [], message: <span>Synced {itemName} cannot be copied for the following project, since this project has no {itemName}.</span> };
        }
        if (summary.exceedLimit.length) {
            return {
                validIds: [],
                message: <div>
                    <p>Unable to copy {itemName} from the linked project "{summary.exceedLimit[0]}". 
                        Copying would result in a PPM Express schedule with more than {PPMX_TASKS_LIMIT} {itemName}, which exceeds the allowed limit.</p>
                    <p>Please contact our Support Team at&nbsp;<Link href="mailto:support@ppm.express">support@ppm.express</Link> for additional information.</p>
                </div>
            }
        }
        if([SourceType.O365Planner, SourceType.MondayCom].includes(sourceType)) {
            return {
                validIds: summary.validIds,
                message: <div>
                    <p>Synced {itemName} from the linked project "{projects[0].attributes.Name}" will be copied to the PPM Express schedule along with their groups and all synced values.</p>
                    <p>Would you like to proceed with copying {itemName} to PPM Express schedule?</p>
                </div>
            };
        }

        return {
            validIds: summary.validIds,
            message: <div>
                <p>Synced {itemName} from the linked project "{projects[0].attributes.Name}" will be copied into a new group "{SourceType_.getName(sourceType)}" within PPM Express schedule with all their synced values.</p>
                <p>Would you like to proceed with copying {itemName} to PPM Express schedule?</p>
            </div>
        };
    }

    if (summary.validIds.length) {
        const entityName = `project${getPluralSuffix(summary.validIds.length)}`;
        if([SourceType.O365Planner, SourceType.MondayCom].includes(sourceType)) {
            return {
                validIds: summary.validIds,
                message: <div>
                    <p>Synced {itemName} will be copied for the selected {summary.validIds.length} {entityName} will be copied within each PPM Express schedule along with their groups and all synced values.</p>
                    {getInvalidProjectsMessage(summary, itemName)}
                    <p>Would you like to proceed with copying {itemName} to PPM Express schedule for {summary.validIds.length} {entityName}?</p>
                </div>
            }
        }

        return {
            validIds: summary.validIds,
            message: <div>
                <p>Synced {itemName} will be copied for the selected {summary.validIds.length} {entityName} will be copied into a new group "{SourceType_.getName(sourceType)}" within each PPM Express schedule with all their synced values.</p>
                {getInvalidProjectsMessage(summary, itemName)}
                <p>Would you like to proceed with copying {itemName} to PPM Express schedule for {summary.validIds.length} {entityName}?</p>
            </div>
        }
    }
    return { 
        validIds: [], 
        message: <div>
            {getInvalidProjectsMessage(summary, itemName)}
            <p>Please contact our Support Team at&nbsp;<Link href="mailto:support@ppm.express">support@ppm.express</Link> for additional information.</p>
        </div>
    };
}

const getInvalidProjectsMessage = (summary: ProjectsTaskSummary, itemName: string) => <>
    {!!summary.exceedLimit.length && 
        <p>
            <div>Synced {itemName} cannot be copied for the following {summary.exceedLimit.length} project{getPluralSuffix(summary.exceedLimit.length)}, since these project{getPluralSuffix(summary.exceedLimit.length)} contain more than {PPMX_TASKS_LIMIT} tasks:</div>
            {summary.exceedLimit.slice(0, 5).map(_ => `- ${_}`).join('\n')}
        </p>}
    {!!summary.empty.length && 
        <p>
            <div>Synced {itemName} cannot be copied for the following {summary.empty.length} project{getPluralSuffix(summary.empty.length)}, since these project{getPluralSuffix(summary.empty.length)} have no tasks:</div>
            {summary.empty.slice(0, 5).map(_ => `- ${_}`).join('\n')}
        </p>}
    {!!summary.readonly.length && 
        <p>
            <div>Synced {itemName} cannot be copied for the following {summary.readonly.length} project{getPluralSuffix(summary.readonly.length)}, since you don’t have edit permissions:</div>
            {summary.readonly.slice(0, 5).map(_ => `- ${_}`).join('\n')}
        </p>}
</> 

export const getCopyScheduleTitle = (sourceType: SourceType) => {
    if(sourceType === SourceType.Jira) {
        return "Copy all synced Jira work items to PPM Express tasks. Copied work items will be added as tasks to a new group with the Jira name.";
    }
    if(sourceType === SourceType.VSTS) {
        return "Copy all synced Azure DevOps work items to PPM Express tasks. Copied work items will be added as tasks to a new group with the Azure DevOps name.";
    }
    if([SourceType.O365Planner, SourceType.MondayCom].includes(sourceType)) {
        return `Copy all synced ${SourceType_.getName(sourceType)} tasks to PPM Express tasks. Copied tasks will be added together with their groups.`;
    }
    return `Copy all synced ${SourceType_.getName(sourceType)} tasks to PPM Express tasks. Copied tasks will be added to a new group with the ${SourceType_.getName(sourceType)} name.`;
}

export default CopyScheduleConfirmationDialog;